import React, { useState } from 'react';
import { useMutation } from 'react-query';
import * as API from 'api';
import validator from 'validator';

import { withStyles } from '@material-ui/core/styles';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTextField from '@material-ui/core/TextField';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import RowButtons from 'components/TableGroupUsers/RowButtons';
import { useAuth0 } from 'utils/auth0';

import { LABEL_ROLES } from 'utils/constants';
import { Box, Switch, Typography } from '@material-ui/core';

const Users = API.Users();
const Groups = API.Groups();

const EMAIL_INPUT_HELPER_TEXT = 'Invalid Input';

const styles = () => ({
  root: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    '& > *': {
      borderBottom: 'unset',
    },
  },
  edit: {
    fontSize: 'medium',
  },
  expandedRow: {
    backgroundColor: '#f3f3f3',
  },
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  buttonCell: {
    whiteSpace: 'nowrap',
  },
  totalsTable: {
    marginTop: '1rem',
  },
  fileCell: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  totalsCell: {
    border: 0,
    fontWeight: 'bold',
  },
});

const Row = props => {
  const {
    classes,
    group,
    row,
    myUserId,
    row: { email: intialEmailValue, role: intialRoleValue },
    refetchUsers,
    isOwner,
  } = props;

  const [isEditingRow, setIsEditingRow] = useState(false);
  const [hasInputEmailError, setHasInputEmailError] = useState(false);
  const [emailInput, setEmailInput] = useState(intialEmailValue);
  const [roleInput, setRoleInput] = useState(intialRoleValue);

  const { logout } = useAuth0();

  const deleteUserMutation = useMutation(id => Users.deleteUser(id), {
    onSuccess: () => {
      refetchUsers();
    },
    onError: error => {
      alert(error);
      deleteUserMutation.reset();
    },
  });

  const deleteUser = async user => {
    if (confirm(`Are you sure you want to delete ${user.email}?`)) {
      await deleteUserMutation.mutate(user.id);
    }
    await refetchUsers();
  };

  // 2 endpoints/mutations for updating users here since the change email endpoint has to operate on Auth0
  const updateUserMutation = useMutation(({ userId, data }) => Groups.updateUserRole(group.id, userId, data), {
    onError: error => {
      alert(error.data.detail);
      updateUserMutation.reset();
    },
  });
  const changeUserEmailMutation = useMutation(({ id, emailInput }) => Users.updateUserEmail(id, emailInput), {
    onSuccess: () => {
      refetchUsers();
    },
    onError: error => {
      alert(error);
      changeUserEmailMutation.reset();
    },
  });

  const validateEmailInput = emailInput => {
    const isEmailInputInvalid = !validator.isEmail(emailInput);
    setHasInputEmailError(isEmailInputInvalid);

    return isEmailInputInvalid;
  };

  const handleEditClick = () => setIsEditingRow(true);

  const handleActAsClick = async targetUser => {
    //Don't love alert/confirm but it is already used
    if (
      window.confirm(
        'Doing this will log you out and and you will be acting as another user. \n Do you want to continue?'
      )
    ) {
      await Users.actAsUser(myUserId, targetUser.id);
      logout();
    }
  };

  const handleCancelClick = () => {
    setIsEditingRow(false);
    setHasInputEmailError(false);
    setEmailInput(intialEmailValue);
  };

  const handleSaveClick = async e => {
    const isEmailInputValid = !validateEmailInput(emailInput);
    e.preventDefault();

    if (roleInput !== intialRoleValue) {
      await updateUserMutation.mutateAsync({
        userId: row.id,
        data: { role: roleInput },
      });
    }

    if (emailInput === intialEmailValue) {
      // input hasn't changed, so don't save
      handleCancelClick();
    } else if (isEmailInputValid) {
      await changeUserEmailMutation.mutate({ id: row.id, emailInput });
    }

    await refetchUsers();
  };

  const handleSetEmailInput = e => {
    e.preventDefault();
    const emailInputValue = e.target.value;
    validateEmailInput(emailInputValue);
    setEmailInput(emailInputValue);
  };

  const handleSetRoleInput = e => {
    e.preventDefault();
    setRoleInput(e.target.value);
  };

  const verifyKycMutation = useMutation(({ id, verify }) => Users.verifyKyc(id, verify), {
    onSuccess: () => {
      refetchUsers();
    },
    onError: error => {
      alert(error);
      verifyKycMutation.reset();
    },
  });

  const EmailInputCell = isEditingRow ? (
    <MuiTextField
      error={hasInputEmailError}
      value={emailInput}
      onChange={handleSetEmailInput}
      variant="outlined"
      size="small"
      type="text"
      helperText={hasInputEmailError && EMAIL_INPUT_HELPER_TEXT}
    />
  ) : (
    <span>{row.email}</span>
  );

  const RoleDropdownCell = isEditingRow ? (
    <MuiFormControl variant="outlined" size="small">
      <MuiInputLabel>Role</MuiInputLabel>
      <MuiSelect label="Role" value={roleInput} variant="outlined" onChange={handleSetRoleInput}>
        <MuiMenuItem value={LABEL_ROLES.get('admin')}>Label Admin</MuiMenuItem>
        <MuiMenuItem value={LABEL_ROLES.get('member')}>Label Member</MuiMenuItem>
      </MuiSelect>
    </MuiFormControl>
  ) : (
    row.role
  );

  return (
    <MuiTableRow className={classes.root}>
      <MuiTableCell component="th" scope="row">
        {row.id}
      </MuiTableCell>
      <MuiTableCell>
        {EmailInputCell}
        {isOwner && (
          <Box>
            <Typography style={{ fontSize: '0.55rem', fontWeight: 700 }}>OWNER</Typography>
          </Box>
        )}
      </MuiTableCell>
      <MuiTableCell style={{ width: '20%' }}>{row.name}</MuiTableCell>
      <MuiTableCell>{RoleDropdownCell}</MuiTableCell>
      <MuiTableCell style={{ width: '20%' }}>{row?.accepted_terms_at?.split('T')[0]}</MuiTableCell>
      <MuiTableCell>
        <Box>
          <Switch
            onChange={(_e, value) => {
              verifyKycMutation.mutate({ id: row?.id, verify: value });
            }}
            checked={row?.kyc_verified_at ? true : false}
          />
        </Box>
      </MuiTableCell>
      <MuiTableCell align="right">
        <RowButtons
          group={group}
          row={row}
          handleCancelClick={handleCancelClick}
          handleDeleteClick={() => deleteUser(row)}
          handleEditClick={handleEditClick}
          handleSaveClick={handleSaveClick}
          isEditingRow={isEditingRow}
          isLoadingSubmit={changeUserEmailMutation.isLoading}
          handleActAsClick={() => handleActAsClick(row)}
        />
      </MuiTableCell>
    </MuiTableRow>
  );
};

export default withStyles(styles)(Row);
